// src/pages/admin/AuditLogs.tsx
import React, { useState, useEffect } from 'react';
import {
  Activity, Search, Filter, Download, Eye,
  User, Clock, FileText, Settings, Shield,
  ChevronDown, RefreshCw, Database,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Select, Input, Pagination, Modal } from '../../components/ui';
import { adminService, ActivityLog, LogFilter } from '../../services/admin';
import toast from 'react-hot-toast';

const eventColors: Record<string, { bg: string; text: string; label: string }> = {
  created: { bg: 'bg-success/10', text: 'text-success', label: 'Dibuat' },
  updated: { bg: 'bg-info/10', text: 'text-info', label: 'Diubah' },
  deleted: { bg: 'bg-danger/10', text: 'text-danger', label: 'Dihapus' },
  validated: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Validasi' },
  login: { bg: 'bg-teal-main/10', text: 'text-teal-main', label: 'Login' },
  logout: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Logout' },
  default: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Log' },
};

const subjectIcons: Record<string, React.ReactNode> = {
  'App\\Models\\LaporanMasalah': <FileText className="w-4 h-4" />,
  'App\\Models\\User': <User className="w-4 h-4" />,
  'App\\Models\\ValidasiLog': <Shield className="w-4 h-4" />,
  'App\\Models\\SystemSetting': <Settings className="w-4 h-4" />,
  'Auth': <Shield className="w-4 h-4" />,
};

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, debouncedSearch, eventFilter, dateFrom, dateTo]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const filters: LogFilter = {
        page: currentPage,
        per_page: 10,
        search: debouncedSearch,
        log_name: eventFilter,
        date_from: dateFrom,
        date_to: dateTo,
      };

      const res = await adminService.logs.getAll(filters);
      setLogs(res.data);
      setTotalPages(res.meta.last_page);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Gagal memuat log aktivitas');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const filters: LogFilter = {
        search: debouncedSearch,
        log_name: eventFilter,
        date_from: dateFrom,
        date_to: dateTo,
      };
      const arrayBuffer = await adminService.logs.export(filters);
      const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Gagal mengexport log');
    }
  };

  const getEventConfig = (eventName: string) => {
    return eventColors[eventName] || eventColors.default;
  };

  const getSubjectIcon = (subjectType: string) => {
    // Simple mapping mostly for visual cue
    if (subjectType.includes('Laporan')) return subjectIcons['App\\Models\\LaporanMasalah'];
    if (subjectType.includes('User')) return subjectIcons['App\\Models\\User'];
    if (subjectType.includes('Setting')) return subjectIcons['App\\Models\\SystemSetting'];
    return <Activity className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Log Aktivitas' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Log Aktivitas</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Riwayat semua aktivitas pengguna dalam sistem
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />} onClick={fetchLogs}>
            Refresh
          </Button>
          <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" />} onClick={handleExport}>
            Export
          </Button>
        </div>
      </div>

      {/* Stats - For now hardcoded or needs separate API for quick stats if expensive to count on backend */}
      {/* We can hide stats or implement if API provides it. For now, let's keep it simple or remove if no data */}

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
            <input
              type="text"
              placeholder="Cari aktivitas atau nama user..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-light"
            />
          </div>
          <Select
            options={[
              { value: '', label: 'Semua Event' },
              { value: 'created', label: 'Dibuat' },
              { value: 'updated', label: 'Diubah' },
              { value: 'deleted', label: 'Dihapus' },
              { value: 'validated', label: 'Validasi' },
              { value: 'login', label: 'Login' },
            ]}
            placeholder="Event"
            value={eventFilter}
            onChange={setEventFilter}
          />
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="Dari" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="Sampai" />
        </div>
      </Card>

      {/* Log List */}
      <Card padding="sm">
        {loading ? (
          <div className="py-10 text-center text-neutral-text-secondary">Memuat log...</div>
        ) : logs.length === 0 ? (
          <div className="py-10 text-center text-neutral-text-secondary">Tidak ada log aktivitas ditemukan</div>
        ) : (
          <div className="space-y-1">
            {logs.map((log) => {
              const eventConfig = getEventConfig(log.log_name);
              return (
                <button
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-neutral-bg transition-colors text-left"
                >
                  {/* Event Badge */}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${eventConfig.bg} ${eventConfig.text} shrink-0`}>
                    {eventConfig.label}
                  </span>

                  {/* Subject Icon */}
                  <div className="p-1.5 bg-neutral-bg rounded-lg shrink-0 text-neutral-text-secondary">
                    {getSubjectIcon(log.subject_type)}
                  </div>

                  {/* Description */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-text-primary truncate">
                      {log.description}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">
                      <span className="font-semibold">{log.causer?.name || 'System'}</span>
                      <span className="mx-1">•</span>
                      {log.causer?.role?.name || '-'}
                    </p>
                  </div>

                  {/* Time */}
                  <div className="text-right shrink-0">
                    <p className="text-xs text-neutral-text-secondary">
                      {new Date(log.created_at).toLocaleDateString('id-ID')}
                    </p>
                    <p className="text-xs text-neutral-text-secondary">
                      {new Date(log.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>

                  <Eye className="w-4 h-4 text-neutral-text-secondary shrink-0" />
                </button>
              );
            })}
          </div>
        )}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="📋 Detail Log Aktivitas"
        size="lg"
      >
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Event</p>
                <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-bold ${getEventConfig(selectedLog.log_name).bg} ${getEventConfig(selectedLog.log_name).text}`}>
                  {getEventConfig(selectedLog.log_name).label}
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Subject</p>
                <p className="text-sm font-medium mt-1">{selectedLog.subject_type?.split('\\').pop()} #{selectedLog.subject_id}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">User</p>
                <p className="text-sm font-medium mt-1">{selectedLog.causer?.name || 'System'}</p>
                <p className="text-xs text-neutral-text-secondary">{selectedLog.causer?.role?.name || '-'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Waktu</p>
                <p className="text-sm font-medium mt-1">{new Date(selectedLog.created_at).toLocaleString('id-ID')}</p>
              </div>
              {/* IP and Agent might be in properties if Spatie saves them there, or we need to add fields to interface if backend sends them */}
              {selectedLog.properties?.ip && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">IP Address</p>
                  <p className="text-sm font-mono mt-1">{selectedLog.properties.ip}</p>
                </div>
              )}
              {selectedLog.properties?.user_agent && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Browser</p>
                  <p className="text-sm mt-1">{selectedLog.properties.user_agent}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Deskripsi</p>
              <p className="text-sm bg-neutral-bg p-3 rounded-xl">{selectedLog.description}</p>
            </div>

            {selectedLog.properties && Object.keys(selectedLog.properties).length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">Properties (Data)</p>
                <pre className="text-xs bg-teal-dark text-white p-4 rounded-xl overflow-x-auto font-mono">
                  {JSON.stringify(selectedLog.properties, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t border-neutral-border">
              <Button variant="outline" onClick={() => setSelectedLog(null)}>Tutup</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogs;